/**
 * Domain model types for the Evaluation system
 */

/**
 * Importance levels for criteria with their scoring semantics
 */
export enum Importance {
  /** Must-have requirements (dealbreakers) */
  Critical = 'Critical',
  /** Need-to-have features */
  High = 'High',
  /** Impactful nice-to-haves */
  Medium = 'Medium',
  /** Low-impact nice-to-haves */
  Low = 'Low',
  /** Informational only, not included in scoring calculations */
  None = 'None',
}

/**
 * Score values with their meanings:
 * - null: Not scored yet (default/initial state)
 * - 0: Skip (explicitly chosen not to score)
 * - 1: Unacceptable (solution fails to meet this requirement)
 * - 2: Does not meet expectations
 * - 3: Partially meets expectations
 * - 4: Meets Expectations
 * - 5: Exceeds Expectations
 */
export type ScoreValue = 0 | 1 | 2 | 3 | 4 | 5 | null;

/**
 * Value scale types for mapping raw scores to normalized values (0-1)
 */
export type ValueScale = 'linear_1_to_5';

/**
 * Default value scale used throughout the application
 */
export const DEFAULT_VALUE_SCALE: ValueScale = 'linear_1_to_5';

/**
 * Importance weights used for MCDA criterion weight calculation
 */
export const IMPORTANCE_WEIGHTS = {
  [Importance.None]: 0,
  [Importance.Low]: 1,
  [Importance.Medium]: 2,
  [Importance.High]: 4,
  [Importance.Critical]: 8,
} as const;

/**
 * Individual criterion for evaluation
 */
export interface Criterion {
  /** Unique identifier (UUID) */
  id: string;
  /** Criterion title */
  title: string;
  /** Detailed description of what to evaluate */
  description: string;
  /** Importance level affecting scoring weight */
  importance: Importance;
  /** Optional free-form text for grouping criteria during scoring */
  category: string | null;
  /** Optional helpful hints for evaluators */
  helpfulHints?: string;

  // MCDA fields (optional for backward compatibility, calculated when needed)
  /** Auto-calculated weight within group (0-1), sum to 1 within group. Derived from importance. */
  criterionWeight?: number;
  /** Value scale for mapping raw scores to normalized values (0-1) */
  valueScale?: ValueScale;
}

/**
 * Group of related criteria with an assigned weight
 */
export interface CriteriaGroup {
  /** Unique identifier (UUID) */
  id: string;
  /** Group name */
  name: string;
  /** Weight for this group (0-100) - DEPRECATED: use groupWeight */
  weight: number;
  /** Group weight in decimal format (0-1), all groups sum to 1 (optional for backward compatibility) */
  groupWeight?: number;
  /** Criteria belonging to this group */
  criteria: Criterion[];
}

/**
 * Solution/vendor being evaluated
 */
export interface Solution {
  /** Unique identifier (UUID) */
  id: string;
  /** Name of the solution */
  solutionName: string;
  /** Vendor/provider name (optional) */
  vendorName?: string;
}

/**
 * Participant role/permission level
 */
export enum ParticipantType {
  /** Created the evaluation, has full permissions */
  Host = 'Host',
  /** Can score assigned criteria groups */
  Evaluator = 'Evaluator',
  /** Read-only access to view results */
  Viewer = 'Viewer',
}

/**
 * Invitation status for participants
 */
export enum InvitationStatus {
  /** Invitation sent, awaiting response */
  Pending = 'Pending',
  /** Participant accepted invitation */
  Accepted = 'Accepted',
  /** Participant declined invitation */
  Declined = 'Declined',
}

/**
 * Evaluation lifecycle status
 */
export enum EvaluationStatus {
  /** Evaluation created, but criteria and participants not yet configured */
  Organizing = 'Organizing',
  /** Ready for scoring (requires ≥1 solution, ≥1 criteria group with ≥1 criterion) */
  Active = 'Active',
  /** Winner selected, evaluation ended, scoring locked */
  Concluded = 'Concluded',
  /** Unfinished/terminated evaluation, scoring locked */
  Closed = 'Closed',
}

/**
 * Participant in an evaluation
 */
export interface Participant {
  /** Unique identifier (UUID) */
  id: string;
  /** Participant full name */
  name: string;
  /** Contact email */
  email: string;
  /** Optional cell phone */
  phone?: string;
  /** Role/permission level */
  participantType: ParticipantType;
  /**
   * Which criteria groups this participant can score
   * - null: can score all groups (typical for Host)
   * - []: cannot score any groups (Viewer)
   * - [...groupIds]: can score only specific groups
   */
  criteriaGroupAssignments: string[] | null;
  /** When participant was invited */
  invitedDate: Date;
  /** When invitation expires (null for accepted invitations) */
  invitationExpirationDate: Date | null;
  /** Last time participant took any action */
  lastActivityDate: Date | null;
  /** Invitation status (mocked until backend integration) */
  invitationStatus: InvitationStatus;
}

/**
 * Main evaluation entity
 */
export interface Evaluation {
  /** Unique identifier (UUID) */
  id: string;
  /** Evaluation name */
  name: string;
  /** Optional high-level description of the evaluation purpose (max 1024 characters) */
  summary?: string;
  /** Optional target completion date */
  targetCompletionDate: Date | null;
  /** Solutions being evaluated */
  targetSolutions: Solution[];
  /** Criteria groups for this evaluation */
  criteriaGroups: CriteriaGroup[];
  /** Participants in this evaluation */
  participants: Participant[];
  /** Default days until invitation expires (default: 7) */
  invitationExpirationDays: number;
  /** Lifecycle status of the evaluation */
  status: EvaluationStatus;
  /** When the status was last changed */
  statusChangedDate: Date;
  /** Optional reason for closure (only when status is Closed) */
  closedReason?: string | null;
  /** When the evaluation was created */
  createdDate: Date;
  /** When the evaluation was last modified */
  modifiedDate: Date;
}

/**
 * Reusable template for creating evaluations
 */
export interface EvaluationTemplate {
  /** Unique identifier (UUID) */
  id: string;
  /** Template name */
  templateName: string;
  /** Criteria groups in this template */
  criteriaGroups: CriteriaGroup[];
  /** When the template was last modified */
  modifiedDate: Date;
}

/**
 * Individual score for a criterion
 */
export interface CriteriaScore {
  /** ID of the criterion being scored */
  criteriaId: string;
  /** Score value (null = not scored yet) */
  score: ScoreValue;
}

/**
 * Participant's scorecard for an evaluation
 */
export interface Scorecard {
  /** Unique identifier (UUID) */
  id: string;
  /** ID of the evaluation this scorecard belongs to */
  evaluationId: string;
  /** ID of the participant who owns this scorecard */
  participantId: string;
  /** ID of the solution being scored */
  solutionId: string;
  /** Name of the solution being scored */
  solutionName: string;
  /** Name of the participant */
  participantName: string;
  /** Individual criterion scores */
  scores: CriteriaScore[];
  /** Percentage of criteria scored (0-100) */
  completionPercentage: number;
  /** Whether all scoring is complete */
  isComplete: boolean;
  /** Whether the scorecard has been submitted (locked for editing) */
  isSubmitted: boolean;
  /** When the scorecard was submitted (null if not submitted) */
  submittedDate: Date | null;
  /** When the scorecard was last modified */
  lastModified: Date;
}
